import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Calendar, Sparkles, TrendingUp, Bell, Lock, Clock, BarChart3, Flame, Heart, Shield } from 'lucide-react';
export function LandingPage() {
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };
  return <div className="relative w-full overflow-hidden bg-[#0a0a0a]" data-id="element-254">
      <div className="absolute inset-0 bg-noise z-0" data-id="element-255"></div>

      {/* Subtle ambient glow for the hero */}
      <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-centurion-500/10 rounded-full blur-[120px] pointer-events-none z-0" data-id="element-256"></div>

      {/* Hero */}
      <section className="relative z-10 pt-32 pb-24 md:pt-48 md:pb-32 px-6 max-w-6xl mx-auto text-center" data-id="element-257">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} data-id="element-258">
          <h1 className="font-mono text-5xl md:text-7xl lg:text-8xl font-bold text-gray-100 leading-tight mb-6 tracking-tighter uppercase" data-id="element-259">
            Just show up.
            <br data-id="element-260" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-centurion-400 to-amber-600" data-id="element-261">
              Every single day.
            </span>
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10 font-light leading-relaxed" data-id="element-262">
            habbit is a habit challenge with an AI system that helps you stay
            consistent — not by punishing you, but by making sure you never miss
            a day. Set your challenge, define your habits, and let the
            intelligence layer keep you on track.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-263">
            <Link to="/app" className="w-full sm:w-auto bg-centurion-500 hover:bg-centurion-400 text-surface-600 px-8 py-4 rounded-full text-lg font-bold tracking-wide transition-all flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_30px_rgba(245,158,11,0.5)]" data-id="element-264">
              Start Your Challenge <ArrowRight size={20} data-id="element-265" />
            </Link>
            <a href="#how-it-works" className="w-full sm:w-auto bg-transparent border border-surface-100 hover:border-surface-50 text-gray-200 px-8 py-4 rounded-full text-lg font-medium transition-all flex items-center justify-center hover:bg-surface-300/50" data-id="element-266">
              How It Works
            </a>
          </div>
        </motion.div>

        {/* Abstract App Preview */}
        <motion.div initial={{
        opacity: 0,
        y: 50
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 1,
        delay: 0.2
      }} className="mt-24 mx-auto max-w-4xl bg-surface-400/40 border border-surface-200/50 rounded-2xl p-4 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-md relative overflow-hidden" data-id="element-267">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-centurion-500/50 to-transparent" data-id="element-268"></div>

          <div className="flex items-center gap-2 mb-6 px-2" data-id="element-269">
            <div className="w-3 h-3 rounded-full bg-surface-200 border border-surface-100" data-id="element-270"></div>
            <div className="w-3 h-3 rounded-full bg-surface-200 border border-surface-100" data-id="element-271"></div>
            <div className="w-3 h-3 rounded-full bg-surface-200 border border-surface-100" data-id="element-272"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-273">
            <div className="col-span-2 space-y-3" data-id="element-274">
              {['No Sugar', 'Protein — 120g', 'Workout — 45 min', 'Reading — 10 pages'].map((label, i) => <div key={i} className="h-14 bg-surface-300/80 rounded-lg border border-surface-200 flex items-center px-4 justify-between hover:border-centurion-500/30 transition-colors" data-id="element-275">
                  <div className="flex items-center gap-3" data-id="element-276">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${i < 2 ? 'border-emerald-500 bg-emerald-500/10 shadow-[0_0_10px_rgba(16,185,129,0.2)]' : 'border-surface-100'}`} data-id="element-277">
                      {i < 2 && <CheckCircle2 size={12} className="text-emerald-500" data-id="element-278" />}
                    </div>
                    <span className={`text-sm font-medium ${i < 2 ? 'text-gray-200' : 'text-gray-400'}`} data-id="element-279">
                      {label}
                    </span>
                  </div>
                  {i < 2 && <span className="text-xs text-emerald-500 font-mono-num font-bold tracking-wider" data-id="element-280">
                      DONE
                    </span>}
                  {i >= 2 && <div className="w-16 h-2 bg-surface-100 rounded-full" data-id="element-281"></div>}
                </div>)}
            </div>
            <div className="col-span-1 space-y-4" data-id="element-282">
              <div className="h-32 bg-gradient-to-br from-centurion-900/20 to-surface-400 border border-centurion-500/20 rounded-lg p-4 flex flex-col justify-center items-center relative overflow-hidden" data-id="element-283">
                <div className="absolute top-0 right-0 w-16 h-16 bg-centurion-500/10 blur-xl rounded-full" data-id="element-284"></div>
                <div className="text-4xl font-mono-num font-bold text-centurion-500 mb-1 tracking-tighter" data-id="element-285">
                  DAY 23
                </div>
                <div className="text-xs text-gray-500 uppercase tracking-widest font-mono" data-id="element-286">
                  of 75
                </div>
              </div>
              <div className="h-32 bg-surface-300/80 border border-surface-200 rounded-lg p-4 flex flex-col justify-center items-center text-center hover:border-centurion-500/30 transition-colors" data-id="element-287">
                <Sparkles size={20} className="text-centurion-400 mb-3" data-id="element-288" />
                <div className="text-xs text-gray-400 leading-relaxed font-medium" data-id="element-289">
                  AI coaching insight ready
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* The Philosophy */}
      <section className="relative z-10 py-24 bg-surface-500/20 border-y border-surface-200/30" data-id="element-290">
        <div className="max-w-4xl mx-auto px-6" data-id="element-291">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="text-center mb-16" data-id="element-292">
            <h2 className="font-mono text-3xl md:text-4xl font-bold text-gray-100 mb-6 uppercase tracking-tight" data-id="element-293">
              The problem with most challenges
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto leading-relaxed text-lg" data-id="element-294">
              75 Hard. 100-day challenges. They all have the same flaw: miss one
              day and you're back to zero. The fear of resetting stops people
              from even starting — and when they do fail, they quit entirely.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-surface-400/80 border border-surface-200 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-295">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-centurion-500/50 to-transparent" data-id="element-296"></div>

            <h3 className="font-mono text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-100 to-gray-400 mb-6 uppercase tracking-tight" data-id="element-297">
              habbit flips the model.
            </h3>
            <p className="text-gray-400 max-w-xl mx-auto leading-relaxed mb-10 text-lg" data-id="element-298">
              Instead of punishing you for falling short, habbit's intelligence
              layer steps in before you miss. It adjusts, it nudges, it meets
              you where you are. The only thing that matters is that you show
              up.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto" data-id="element-299">
              {[{
              icon: Bell,
              label: '4pm Nudge',
              desc: "Haven't logged yet? The system reaches out with what you can still do today."
            }, {
              icon: Heart,
              label: 'Adjusted Targets',
              desc: 'Tough day? The AI suggests a lighter version so you can still show up.'
            }, {
              icon: Shield,
              label: 'Streak Protected',
              desc: 'Partial completion beats zero. Your streak stays alive as long as you show up.'
            }].map((item, i) => {
              const Icon = item.icon;
              return <motion.div key={i} initial={{
                opacity: 0,
                y: 15
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: i * 0.1
              }} className="bg-surface-300/50 border border-surface-200 rounded-xl p-6 hover:bg-surface-300 transition-colors" data-id="element-300">
                    <div className="w-10 h-10 rounded-lg bg-centurion-500/10 flex items-center justify-center mx-auto mb-4" data-id="element-301">
                      <Icon size={18} className="text-centurion-500" data-id="element-302" />
                    </div>
                    <div className="text-sm font-bold text-gray-200 mb-2 font-mono uppercase tracking-wide" data-id="element-303">
                      {item.label}
                    </div>
                    <div className="text-xs text-gray-400 leading-relaxed" data-id="element-304">
                      {item.desc}
                    </div>
                  </motion.div>;
            })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Challenge Durations */}
      <section className="relative z-10 py-24 max-w-6xl mx-auto px-6" data-id="element-305">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="text-center mb-16" data-id="element-306">
          <h2 className="font-mono text-3xl md:text-4xl font-bold text-gray-100 mb-4 uppercase tracking-tight" data-id="element-307">
            Your Challenge. Your Duration.
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg" data-id="element-308">
            Pick a timeframe that matches your ambition. Define the habits that
            matter to you. habbit adapts.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-309">
          {[{
          days: 30,
          label: 'Starter',
          desc: 'Build the foundation'
        }, {
          days: 75,
          label: '75 Days',
          desc: 'The classic challenge'
        }, {
          days: 100,
          label: 'Centurion',
          desc: 'Own the century'
        }, {
          days: 365,
          label: 'Full Year',
          desc: 'Transform everything'
        }].map((challenge, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.08
        }} className="bg-surface-400/50 border border-surface-200 rounded-xl p-8 text-center hover:border-centurion-500/40 transition-all hover:-translate-y-1 group" data-id="element-310">
              <div className="text-5xl font-mono-num font-bold text-centurion-500 mb-3 tracking-tighter group-hover:scale-110 transition-transform" data-id="element-311">
                {challenge.days}
              </div>
              <div className="text-sm font-bold text-gray-200 mb-2 font-mono uppercase tracking-wide" data-id="element-312">
                {challenge.label}
              </div>
              <div className="text-xs text-gray-500" data-id="element-313">{challenge.desc}</div>
            </motion.div>)}
        </div>
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-center text-gray-500 text-sm mt-8 font-mono" data-id="element-314">
          &gt; Or set any custom duration. It's your commitment.
        </motion.p>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative z-10 py-24 bg-surface-500/20 border-y border-surface-200/30" data-id="element-315">
        <div className="max-w-6xl mx-auto px-6" data-id="element-316">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="mb-20" data-id="element-317">
            <h2 className="font-mono text-3xl md:text-4xl font-bold text-gray-100 mb-4 uppercase tracking-tight" data-id="element-318">
              How habbit Works
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-319">
            {[{
            step: '01',
            title: 'Log Daily',
            icon: Calendar,
            desc: 'Track your habits every day. Check off what you completed, log your numbers, and finalize your day.'
          }, {
            step: '02',
            title: 'Get Coached',
            icon: Sparkles,
            desc: 'An agentic health operating system learns your patterns, nudges you before you miss, and adjusts targets on tough days.'
          }, {
            step: '03',
            title: 'Build Habits',
            icon: TrendingUp,
            desc: 'Consistency compounds. Complete your challenge and carry the discipline forward into lasting change.'
          }].map((item, i) => {
            const Icon = item.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="relative group" data-id="element-320">
                  <div className="text-8xl font-mono-num font-bold text-surface-200 absolute -top-12 -left-6 z-0 select-none opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-321">
                    {item.step}
                  </div>
                  <div className="relative z-10" data-id="element-322">
                    <div className="w-14 h-14 rounded-xl bg-surface-300 border border-surface-200 flex items-center justify-center mb-6 group-hover:border-centurion-500/50 transition-colors" data-id="element-323">
                      <Icon size={24} className="text-centurion-500" data-id="element-324" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-100 mb-3 font-mono uppercase tracking-tight" data-id="element-325">
                      {item.title}
                    </h4>
                    <p className="text-gray-400 leading-relaxed" data-id="element-326">{item.desc}</p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Intelligence Layer */}
      <section className="relative z-10 py-24 max-w-6xl mx-auto px-6" data-id="element-327">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-328">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} data-id="element-329">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface-400/50 border border-surface-200 text-xs font-mono text-gray-400 uppercase tracking-widest mb-8" data-id="element-330">
              <Sparkles size={14} className="text-centurion-500" data-id="element-331" /> Agentic
              intelligence layer
            </div>
            <h2 className="font-mono text-4xl md:text-5xl font-bold text-gray-100 mb-6 uppercase tracking-tight leading-tight" data-id="element-332">
              It doesn't just track.
              <br data-id="element-333" />
              <span className="text-centurion-500" data-id="element-334">It thinks.</span>
            </h2>
            <p className="text-gray-400 leading-relaxed mb-8 text-lg" data-id="element-335">
              habbit's operating system continuously learns from your daily
              patterns. It knows when you're falling behind, when your targets
              need adjusting, and when a simple nudge is all you need to keep
              going.
            </p>
            <div className="space-y-5" data-id="element-336">
              {['Proactive nudges before you miss — not after', 'Adjusts daily targets based on your capacity', 'Morning actions tailored to your goals', 'Evening reflections that surface what matters', 'Weekly coaching summaries with trend insights'].map((item, i) => <div key={i} className="flex items-start gap-4" data-id="element-337">
                  <div className="w-6 h-6 rounded-full bg-surface-300 flex items-center justify-center shrink-0 mt-0.5" data-id="element-338">
                    <CheckCircle2 size={14} className="text-centurion-500" data-id="element-339" />
                  </div>
                  <span className="text-gray-300" data-id="element-340">{item}</span>
                </div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-surface-400/40 border border-surface-200 rounded-2xl p-6 shadow-2xl space-y-5 backdrop-blur-sm relative" data-id="element-341">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-centurion-500/20 via-centurion-500 to-centurion-500/20 rounded-t-2xl" data-id="element-342"></div>

            {/* Simulated AI conversation */}
            <div className="flex items-center gap-3 border-b border-surface-200 pb-4" data-id="element-343">
              <div className="w-8 h-8 rounded-full bg-centurion-500/20 flex items-center justify-center" data-id="element-344">
                <Sparkles size={14} className="text-centurion-400" data-id="element-345" />
              </div>
              <span className="text-gray-200 font-bold font-mono text-sm uppercase tracking-wide" data-id="element-346">
                habbit intelligence
              </span>
              <span className="text-xs text-gray-500 ml-auto font-mono-num" data-id="element-347">
                4:02 PM
              </span>
            </div>

            <div className="bg-surface-300/80 rounded-lg p-5 text-sm text-gray-300 leading-relaxed border border-surface-200" data-id="element-348">
              Hey — it's 4pm and you haven't logged today yet. You've completed
              22 days straight. Here's what I'd suggest:
            </div>

            <div className="space-y-3 pl-2" data-id="element-349">
              <div className="flex items-center gap-3 text-sm text-gray-400" data-id="element-350">
                <div className="w-1.5 h-1.5 rounded-full bg-centurion-500" data-id="element-351"></div>
                <span data-id="element-352">15-minute walk instead of 45-min workout</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400" data-id="element-353">
                <div className="w-1.5 h-1.5 rounded-full bg-centurion-500" data-id="element-354"></div>
                <span data-id="element-355">5 pages of reading before bed</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400" data-id="element-356">
                <div className="w-1.5 h-1.5 rounded-full bg-centurion-500" data-id="element-357"></div>
                <span data-id="element-358">Log your meals — even if imperfect</span>
              </div>
            </div>

            <div className="bg-centurion-900/20 border border-centurion-500/30 rounded-lg p-4 text-center mt-6 flex items-center justify-center gap-2" data-id="element-359">
              <div className="w-2 h-2 rounded-full bg-centurion-500 animate-pulse" data-id="element-360"></div>
              <span className="text-centurion-400 text-sm font-bold font-mono uppercase tracking-wide" data-id="element-361">
                Just show up. Your streak is worth it.
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="relative z-10 py-24 bg-surface-500/20 border-y border-surface-200/30" data-id="element-362">
        <div className="max-w-6xl mx-auto px-6" data-id="element-363">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-center mb-16" data-id="element-364">
            <h2 className="font-mono text-3xl md:text-4xl font-bold text-gray-100 mb-4 uppercase tracking-tight" data-id="element-365">
              Everything You Need
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto text-lg" data-id="element-366">
              A focused toolkit for building consistency. Nothing more, nothing
              less.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-367">
            {[{
            icon: Calendar,
            title: 'Custom Challenges',
            desc: 'Set any duration — 30, 75, 100, or 365 days. Your commitment, your timeline.'
          }, {
            icon: CheckCircle2,
            title: 'Flexible Habits',
            desc: 'Define the habits that matter to you. Boolean checks, numeric targets, whatever fits your life.'
          }, {
            icon: Bell,
            title: 'Smart Nudges',
            desc: 'The system reaches out before you miss — not after. Proactive, not punitive.'
          }, {
            icon: Sparkles,
            title: 'AI Coaching',
            desc: 'Morning actions, evening reflections, and weekly summaries from an agentic health operating system.'
          }, {
            icon: BarChart3,
            title: 'Analytics',
            desc: 'Track completion rates, streaks, and trends. See your progress in charts and data.'
          }, {
            icon: Heart,
            title: 'Adaptive Targets',
            desc: 'Tough day? The AI suggests lighter targets so you can still show up and keep your streak.'
          }].map((feature, i) => {
            const Icon = feature.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.06
            }} className="bg-surface-400/50 border border-surface-200 rounded-xl p-8 hover:bg-surface-300 transition-colors relative overflow-hidden group" data-id="element-368">
                  <div className="absolute top-0 left-0 w-full h-1 bg-surface-200 group-hover:bg-centurion-500/50 transition-colors" data-id="element-369"></div>
                  <Icon size={24} className="text-centurion-500 mb-5" data-id="element-370" />
                  <h4 className="text-lg font-bold text-gray-100 mb-3 font-mono uppercase tracking-wide" data-id="element-371">
                    {feature.title}
                  </h4>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-372">
                    {feature.desc}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 py-32 text-center px-6" data-id="element-373">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-centurion-500/5 rounded-full blur-[100px] pointer-events-none z-0" data-id="element-374"></div>
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="relative z-10" data-id="element-375">
          <h2 className="font-mono text-4xl md:text-6xl font-bold text-gray-100 mb-6 uppercase tracking-tighter" data-id="element-376">
            You don't need to be perfect.
          </h2>
          <p className="text-xl text-gray-400 mb-12 max-w-lg mx-auto font-light" data-id="element-377">
            You just need to show up. Every day. habbit takes care of the rest.
          </p>
          <Link to="/app" className="inline-flex bg-centurion-500 hover:bg-centurion-400 text-surface-600 px-12 py-5 rounded-full text-xl font-bold tracking-wide transition-all items-center justify-center gap-3 shadow-[0_0_30px_rgba(245,158,11,0.2)] hover:shadow-[0_0_50px_rgba(245,158,11,0.4)] hover:-translate-y-1" data-id="element-378">
            Start Your Challenge <ArrowRight size={24} data-id="element-379" />
          </Link>
        </motion.div>
      </section>
    </div>;
}